import { useTranslation } from "react-i18next";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import X from "lucide-react/dist/esm/icons/x";
import type { ReactNode } from "react";

import { cn, tv } from "@/lib/utils";

import { Button, Heading, Text } from "@/components/base";

type Props = DialogPrimitive.DialogProps & {
  trigger?: ReactNode;
  title?: ReactNode;
  description?: ReactNode;
  size?: "sm" | "md" | "lg";
  footer?: ReactNode;
  children?: ReactNode;
  contentClassName?: string;
  bodyClassName?: string;
};

const dialogVariants = tv({
  base: [
    "animate-modal-in",
    "fixed top-1/2 left-1/2 z-50 w-[calc(100%-2rem)] -translate-x-1/2 -translate-y-1/2",
    "bg-card rounded-xl",
    "shadow-modal border-border border",
    "app-root",
  ],
  variants: {
    size: {
      sm: "max-w-[420px]",
      md: "max-w-[560px]",
      lg: "max-w-[720px]",
    },
  },
  defaultVariants: {
    size: "md",
  },
});

export const Dialog: React.FC<Props> = ({
  trigger,
  title,
  description,
  size,
  footer,
  children,
  contentClassName,
  bodyClassName,
  ...props
}) => {
  const { t } = useTranslation();

  return (
    <DialogPrimitive.Root {...props}>
      {trigger && (
        <DialogPrimitive.Trigger asChild>{trigger}</DialogPrimitive.Trigger>
      )}
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            "animate-overlay-in",
            "fixed inset-0 z-50 bg-black/40 backdrop-blur-[2px]"
          )}
        />
        <DialogPrimitive.Content
          className={dialogVariants({ size, className: contentClassName })}
        >
          {(title || description) && (
            <div className="border-border flex items-start justify-between gap-4 border-b px-6 py-4">
              <div className="flex flex-col gap-1">
                {title && (
                  <DialogPrimitive.Title asChild>
                    <Heading as="h2" variant="headingLg">
                      {title}
                    </Heading>
                  </DialogPrimitive.Title>
                )}
                {description && (
                  <DialogPrimitive.Description asChild>
                    <Text variant="bodyMd" tone="subdued">
                      {description}
                    </Text>
                  </DialogPrimitive.Description>
                )}
              </div>
              <DialogPrimitive.Close asChild>
                <button
                  aria-label={t("Close")}
                  className={cn(
                    "shrink-0 cursor-pointer rounded-lg p-1.5",
                    "text-subdued",
                    "hover:bg-surface-hovered hover:text-text",
                    "focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none"
                  )}
                >
                  <X className="h-4 w-4" />
                </button>
              </DialogPrimitive.Close>
            </div>
          )}
          {children && (
            <div className={cn("flex flex-col gap-4 px-6 py-5", bodyClassName)}>
              {children}
            </div>
          )}
          {footer && (
            <div className="border-border bg-surface-subdued flex items-center justify-end gap-2 rounded-b-xl border-t px-6 py-4">
              {footer}
            </div>
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};

type ConfirmDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  confirmLabel: ReactNode;
  cancelLabel?: ReactNode;
  tone?: "primary" | "critical";
  onConfirm: () => void;
  loading?: boolean;
};

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  cancelLabel = "Cancel",
  tone = "critical",
  onConfirm,
  loading,
}) => {
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      description={description}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            {cancelLabel}
          </Button>
          <Button
            variant={tone === "critical" ? "critical" : "primary"}
            loading={loading}
            onClick={onConfirm}
          >
            {confirmLabel}
          </Button>
        </>
      }
    />
  );
};
